import React, { useEffect, useState } from "react";
import Button from "@amzn/meridian/button";
import Row from "@amzn/meridian/row";
import Icon from "@amzn/meridian/icon";
import Text from "@amzn/meridian/text";
import Column from "@amzn/meridian/column";
import chevronLeftSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-left-small";
import chevronRightSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-right-small";

import Select, { SelectOption } from "@amzn/meridian/select";
import { Tabs } from "../tabs/Tabs";
import { MailInfoSection } from "./mail-info-section/MailInfoSection";
import { CommentsSection } from "../comments-section/CommentsSection";
import {
    IArtifactDetails,
    useLazyGetArtifactDetailsByIdQuery,
} from "../../endpoints/getArtifactDetailsById";
import { useAppDispatch, useAppSelector } from "../../app/store/hooks";
import {
    UpdateArtifactDetailsByIdRequest,
    useUpdateArtifactDetailsByIdMutation,
} from "../../endpoints/updateArtifactDetailsById";
import { formatLongString } from "../../utils/formattingUtils";
import Loader from "@amzn/meridian/loader";
import { assignedToOptions } from "../../features/filter-sheets/definitions";
import Tag from "@amzn/meridian/tag";
import Alert from "@amzn/meridian/alert";
import { useEditingDocument } from "./hooks";
import { setFilterSheetsOpen } from "../../features/filter-sheets/slice";

export interface IEditDocumentModalProps {
    data: IArtifactDetails[];
    currentRow: number;
}

export const EditDocumentModal: React.FC<IEditDocumentModalProps> = (
    props: IEditDocumentModalProps
) => {
    const { alias } = useAppSelector(state => state.auth.user);
    const dispatch = useAppDispatch();
    const [updateArtifact] = useUpdateArtifactDetailsByIdMutation();
    const [currentRow, setCurrentRow] = useState(props.currentRow);
    const [currentArtifactDetails, setCurrentArtifactDetails] = useState(null);

    const [updatedArtifactDetails, setUpdatedArtifactDetails] =
        useState<UpdateArtifactDetailsByIdRequest>();

    const [status, setStatus] = useState("");
    const [assignedTo, setAssignedTo] = useState("");

    const [getArtifactDetails, { isFetching, isError }] = useLazyGetArtifactDetailsByIdQuery();

    const [isNextButtonDisabled, setIsNextButtonDisabled] = useState(
        props.currentRow === props.data.length - 1
    );

    const [isPreviousButtonDisabled, setIsPreviousButtonDisabled] = useState(
        props.currentRow === 0
    );

    const { setIsEditing, setIsAssignedToChanged, setIsEditSheetOpen } = useEditingDocument();

    const updateButtonState = row => {
        const isFirstRow = row === 0;
        const isLastRow = row === props.data.length - 1;
        setIsPreviousButtonDisabled(isFirstRow);
        setIsNextButtonDisabled(isLastRow);
    };

    const getCurrentRow = async currentRow => {
        await getArtifactDetails({
            artifactType: props.data[currentRow].artifactType,
            artifactId: props.data[currentRow].artifactId,
            businessUseCase: "SMART",
        }).then(response => {
            if (response.data) {
                setCurrentArtifactDetails({
                    ...response.data.artifactDetails,
                    ...response.data.artifactDetails.address,

                    artifactId: response.data.artifactId,
                    artifactType: response.data.artifactType,
                });
            }
        });
    };

    const onPrevious = async () => {
        const updatedCurrentRow = Math.max(0, currentRow - 1);
        setCurrentRow(updatedCurrentRow);
        getCurrentRow(updatedCurrentRow);
        updateButtonState(updatedCurrentRow);
        setIsEditing(false);
    };

    const onNext = () => {
        const updatedCurrentRow = Math.min(props.data.length - 1, currentRow + 1);
        setCurrentRow(updatedCurrentRow);
        getCurrentRow(updatedCurrentRow);
        updateButtonState(updatedCurrentRow);
        setIsEditing(false);
    };

    const onStatusChange = (value: string) => {
        setStatus(value);
        setIsEditing(true);
    };

    const onAssignedToChange = (value: string) => {
        setAssignedTo(value);
        setIsEditing(true);
    };

    const handleUpdateArtifactDetails = async updatedData => {
        try {
            const response = await updateArtifact(updatedData);

            if (response.data && response.data.status === "SUCCEEDED") {
                setUpdatedArtifactDetails(updatedData);
                setStatus(updatedData.updatedArtifactDetails.status);
                setAssignedTo(updatedData.updatedArtifactDetails.assignedTo);
            } else {
                console.error("Failed to update artifact details");
            }
        } catch (error) {
            console.error("Error updating artifact details:", error);
        }
    };

    useEffect(() => {
        dispatch(setFilterSheetsOpen(false));
        setIsEditSheetOpen(true);
        getCurrentRow(props.currentRow);
    }, []);

    useEffect(() => {
        if (currentArtifactDetails) {
            const { status, artifactType, artifactId } = currentArtifactDetails;
            const updatedStatus = status === "UNREAD" ? "OPEN" : status;
            const updatedArtifactDetails = {
                ...currentArtifactDetails,
                status: updatedStatus,
            };

            const newUpdatedData = {
                artifactType,
                artifactId,
                businessUseCase: "SMART",
                clientName: alias,
                updatedBy: alias,
                updatedArtifactDetails,
            };

            if (status === "UNREAD") {
                handleUpdateArtifactDetails(newUpdatedData);
            } else {
                setUpdatedArtifactDetails(newUpdatedData);
                setStatus(updatedStatus);
                setAssignedTo(updatedArtifactDetails.assignedTo);
            }
        } else {
            getCurrentRow(props.currentRow);
        }
    }, [currentArtifactDetails]);

    useEffect(() => {
        if (assignedTo) {
            setIsAssignedToChanged(assignedTo !== currentArtifactDetails?.assignedTo);
        }

        if (currentArtifactDetails) {
            const { artifactType, artifactId } = currentArtifactDetails;
            const updatedStatus = status === "UNREAD" ? "OPEN" : status;
            const updatedArtifactDetails = {
                ...currentArtifactDetails,
                assignedTo: assignedTo,
                status: status,
            };

            const newUpdatedData = {
                artifactType,
                artifactId,
                businessUseCase: "SMART",
                clientName: alias,
                updatedBy: alias,
                updatedArtifactDetails,
            };

            if (status === "UNREAD") {
                handleUpdateArtifactDetails(newUpdatedData);
            } else {
                setUpdatedArtifactDetails(newUpdatedData);
                setStatus(updatedStatus);
                setAssignedTo(updatedArtifactDetails.assignedTo);
            }
        }
    }, [assignedTo, status]);

    return (
        <>
            {updatedArtifactDetails != undefined ? (
                <>
                    <Row width="975px" widths={["34%", "60%", "6%"]}>
                        <Column>
                            <Text type="h300">
                                {formatLongString(
                                    updatedArtifactDetails.updatedArtifactDetails.mailId
                                )}
                            </Text>
                        </Column>
                        <Column>
                            <Row width="70%" widths={["30%", "25%"]} alignmentHorizontal="center">
                                <Button
                                    type={"secondary"}
                                    onClick={onPrevious}
                                    disabled={isPreviousButtonDisabled}
                                >
                                    <Text type="b200">
                                        <Icon tokens={chevronLeftSmallTokens} /> Previous
                                    </Text>
                                </Button>
                                <Button
                                    type={"secondary"}
                                    onClick={onNext}
                                    disabled={isNextButtonDisabled}
                                >
                                    <Text type="b200">
                                        Next <Icon tokens={chevronRightSmallTokens} />
                                    </Text>
                                </Button>
                            </Row>
                        </Column>
                    </Row>
                    <Column width={"100%"}>{isFetching && <Loader type={"linear"} />}</Column>
                    <Row
                        width="100%"
                        widths={["65%", "35%"]}
                        spacingInset="400"
                        alignmentVertical="top"
                    >
                        {!isFetching && (
                            <>
                                <Column>
                                    <iframe
                                        src={
                                            updatedArtifactDetails.updatedArtifactDetails
                                                .documentS3PreSignedUrl
                                        }
                                        title="PDF Viewer"
                                        style={{
                                            width: "604px",
                                            height: "843px",
                                            border: "3px solid black",
                                        }}
                                    />
                                </Column>
                                <Column alignmentVertical="top">
                                    {updatedArtifactDetails.updatedArtifactDetails
                                        .treasureStatus === "ASSOCIATED" ? (
                                        <Column width={"50%"}>
                                            <Tag type="success">Associated</Tag>
                                        </Column>
                                    ) : updatedArtifactDetails.updatedArtifactDetails
                                          .treasureStatus === "UNASSOCIATED" ? (
                                        <Column width={"50%"}>
                                            <Tag type="warning">Unassociated</Tag>
                                        </Column>
                                    ) : updatedArtifactDetails.updatedArtifactDetails
                                          .failureReason === "Unsorted" ? (
                                        <Alert type="error" size="small">
                                            Mail cannot be sorted. Enter mail info manually.
                                        </Alert>
                                    ) : updatedArtifactDetails.updatedArtifactDetails
                                          .failureReason === "Duplicate" ? (
                                        <Alert type="error" size="small">
                                            Same mail has already been uploaded. Change Status to
                                            ‘Closed’ after confirming duplication.
                                        </Alert>
                                    ) : updatedArtifactDetails.updatedArtifactDetails
                                          .failureReason === "Combined" ? (
                                        <Alert type="error" size="small">
                                            File contains two mails. Separate them offline and
                                            upload them again.
                                        </Alert>
                                    ) : null}

                                    <Select
                                        label="Status"
                                        value={status}
                                        onChange={value => onStatusChange(value)}
                                        width={300}
                                    >
                                        {"FAILED" ===
                                            updatedArtifactDetails.updatedArtifactDetails
                                                .status && (
                                            <SelectOption value="FAILED" label="Failed" disabled />
                                        )}
                                        <SelectOption value="OVERDUE" label="Overdue" disabled />
                                        <SelectOption
                                            value="UNREAD"
                                            label="Unread"
                                            disabled={
                                                "Duplicate" ===
                                                    updatedArtifactDetails.updatedArtifactDetails
                                                        .failureReason ||
                                                "Combined" ===
                                                    updatedArtifactDetails.updatedArtifactDetails
                                                        .failureReason
                                            }
                                        />
                                        <SelectOption
                                            value="OPEN"
                                            label="Open"
                                            disabled={
                                                "Duplicate" ===
                                                    updatedArtifactDetails.updatedArtifactDetails
                                                        .failureReason ||
                                                "Combined" ===
                                                    updatedArtifactDetails.updatedArtifactDetails
                                                        .failureReason
                                            }
                                        />
                                        <SelectOption value="CLOSED" label="Closed" />
                                    </Select>

                                    <Select
                                        label="Assigned to"
                                        value={assignedTo}
                                        onChange={value => onAssignedToChange(value)}
                                        placeholder="Enter value..."
                                        width={300}
                                    >
                                        {assignedToOptions.map(option => (
                                            <SelectOption
                                                key={option.value}
                                                value={option.value}
                                                label={option.label}
                                            />
                                        ))}
                                    </Select>
                                    <Tabs
                                        tabs={getTabs(
                                            updatedArtifactDetails.artifactId,
                                            updatedArtifactDetails.artifactType,
                                            updatedArtifactDetails,
                                            assignedTo,
                                            status
                                        )}
                                    />
                                </Column>
                            </>
                        )}
                    </Row>
                </>
            ) : (
                <Column width={"100%"}>
                    {isError ? (
                        <>
                            <Text>Unexpected error occurred while retrieving data.</Text>
                        </>
                    ) : (
                        <Loader type="circular" />
                    )}
                </Column>
            )}
        </>
    );
};

const getTabs = (
    artifactId: string,
    artifactType: string,
    updatedData: UpdateArtifactDetailsByIdRequest,
    assignedTo: string,
    status: string
) => {
    return [
        {
            label: "Mail info",
            value: "information",
            content: (
                <MailInfoSection
                    documentType={updatedData.updatedArtifactDetails.documentType}
                    propertyType={updatedData.updatedArtifactDetails.associatedPropertyType}
                    artifactType={artifactType}
                    artifactId={artifactId}
                    data={updatedData}
                    assignedTo={assignedTo}
                    status={status}
                />
            ),
        },
        {
            label: "Comments",
            value: "comments",
            content: (
                <CommentsSection
                    artifactId={artifactId}
                    artifactType={artifactType}
                    sectionProps={{
                        thumbnailSize: "small",
                        commentedByTextType: "h200",
                        createdOnTextType: "b200",
                    }}
                />
            ),
        },
    ];
};
